export interface Login {
  userId: string;
  actualIncidentId?: string;
  language?: string;
  isTermsAccepted?: boolean;
  sessionId: string;
  suggestedCountryName?: string;
  // Single-use credential for renewing an expired session without re-asserting
  // who we are. Rotated on every exchange, so the value returned here replaces
  // the one that was spent. Optional: /users/refresh omits the profile fields,
  // and an older BE build omits the token entirely.
  refreshToken?: string;
}

export interface UserEntity {
  actualGeo: string;
  actualIncidentId: string;
  id: string;
  language: string;
  telegramChatId: number;
  telegramUserId: number;
  telegramUsername: string;
  termsAccepted: boolean;
  referralCode: string;
  reportedHash: string;
  hashNetwork: string;
  fullName: string;
}

export interface Incident {
  additionsApplied: number;
  appliedAt: Date;
  // decimals: number;
  // JSON number on the wire (BE: 796589520.57). FE never edits these; render
  // via formatAmount pipe to get thousand-separators + M/B compaction.
  estimatedLosesAmount: number;
  estimatedLosesCurrency: string;
  id: string;
  // Display-only short reference: "SMC-{caseNumber}". Always SMC- prefixed for
  // user-app cases. Use for UI; keep `id` (UUID) for routing and API calls.
  caseRef: string;
  caseNumber: number;
  initiatedAt: Date;
  actualOfferId: string;
  hasOpened: boolean;
  leadStatus: 'PREVIEW' | 'SUBMITTED' | 'SCAM' | 'VICTIM' | 'TRANSACTION' | 'EVIDENCE';
  // currency: string;
  scamInfo: ScamInfo;
  status: 'LEAD' | 'NEW' | 'OFFER' | 'OFFER_EXPIRED' | 'REQUEST_OFFER' | 'UPGRADE_PROPOSED' | 'ADMIN_CANCELED' | 'CUSTOMER' | 'INITIAL_INVOICE' | 'CUSTOMER_INVOICE' | 'SUCCESS' | 'CANCELED' | 'DONE';
  totalCostCurrency: string;
  totalReceivedCurrency: string;
  transactionsApplied: number;
  userId: string;
  recoveryProbability: 'EXTREMELY_LOW' | 'LOW' | 'MEDIUM' | 'HIGH' | 'EXTREMELY_HIGH' | null;
  victimInfo: VictimInfo;
  nativeCurrencies: Map<string, string>;
  // Top-level convenience string from BE (joined from scam.scamType.type).
  // Same value as scamInfo.scamTypeId; prefer this for display.
  scamType?: string;
}

export type Incidents = Incident[];

export interface ScamInfo {
  // Human-readable scam category ("Ponzi", "Phishing", "Romance Scam", …)
  // sourced from scam_types.type. NOT a role.
  scamTypeId?: string;
  role?: 'VICTIM' | 'REPRESENTATIVE' | 'LAWYER' | string;
  isPoliceInvolved?: boolean | undefined;
}

export interface VictimInfo {
  victimType?: 'ORGANIZATION' | 'PERSON' | undefined;
  citizenship?: string | undefined;
  residence?: string | undefined;
  incorporationCountry?: string | undefined;
}

// Where the funds sat before the fraudulent transaction. Replaces the
// `isPlatformInvolved` boolean, which could not tell COLD_WALLET and
// THIRD_PARTY apart (SMC-25). Always populated on responses — the BE falls
// back to the legacy flag for rows predating the column.
export type TransactionSource = 'COLD_WALLET' | 'TRADING_PLATFORM' | 'THIRD_PARTY';

export interface Transaction {
  amount: string;
  appliedCurrency: string;
  appliedNetwork: string;
  appliedSenderWallet: string;
  currency: string;
  contractType: string;
  // Token contract address; null for native transfers.
  contractAddress: string | null;
  // decimals: number;
  hash: string;
  id: string;
  incidentId: string;
  source: TransactionSource;
  // Legacy mirror of `source`, still returned for the CSV export and older
  // admin reads. Derived server-side, so it can't disagree — prefer `source`.
  isPlatformInvolved: boolean;
  network: string;
  // User-supplied name for a network we don't list, sent on create when
  // `network` is OTHER. Omitted by the BE (NON_NULL) for known networks.
  customNetworkName?: string;
  receiver: string;
  sender: string;
  timestamp: string;
  // Null when the token could not be priced — meaning "not priced", NOT zero.
  // It used to be written as 0, which claimed the transfer was worth nothing.
  // Render it as unknown and never fold it into a client-side total as 0; the
  // BE already excludes it from the case's estimated-loss tally.
  usdtAmount: string | null;
}

export interface Country {
  id: string;
  name: string;
  flagUrl: string;
}

export interface ContactEvidenceAddition {
  id: string;
  type: string | 'CONTACT';
  knownName: string;
  contacts: [{channel: string, contact: string}];
}

export interface MediaEvidenceAddition {
  id: string;
  type: string | 'MEDIA';
  name: string;
  mediaType: 'DOC' | 'AUDIO' | 'IMG';
  fileId: string;
}

export interface UrlEvidenceAddition {
  id: string;
  type: string | 'URL';
  url: string;
}

export type EvidenceAddition = ContactEvidenceAddition | MediaEvidenceAddition | UrlEvidenceAddition;

export type EvidenceAdditions = (EvidenceAddition)[];

export interface EvidenceInfo {
  id: string;
  incidentId: string;
  accidentDescription: string;
  additions?: EvidenceAdditions;
}

export interface Currency {
  network: string;
  description: string;
}

export interface ParamsCreateTransaction {
  hash: string;
  senderWallet: string;
  network: string;
  source: TransactionSource;
  // Optional on the BE now that `source` carries the choice. Still sent so a
  // FE deploy that lands ahead of the BE one keeps working; safe to drop once
  // the source-aware BE is everywhere.
  isPlatformInvolved: boolean;
  customNetworkName?: string;
}

export interface Offer {
  id:	string;
  incidentId:	string;
  description:	string;
  recoveryProbability:	'EXTREMELY_LOW' | 'LOW' | 'MEDIUM' | 'HIGH' | 'EXTREMELY_HIGH';
  status:	'PROPOSED' | 'UPGRADE_PROPOSED' | 'SATISFIED' | 'PAYMENT_WAITING' | 'EXPIRED' | 'CANCELED';
  packages: Package[];
  preferredPackageId:	string;
  preferredAt: number;
  isApprovedUpgrade:	boolean;
  hasPhoto: boolean;
  upgradedPackages: Package[];
  prevPreferredPackageId:	string;
  prevPreferredAt: number;
  lastInvoiceCompletedAt: number;
  initialPaymentCompletedAt: number;
  initialPaymentAmount: string;
  createdAt:	number;
  proposedAt:	number;
  expiredAt:	number;
  actualInvoiceId:	string;
  paymentCurrencyId:	string;
  // JSON number on the wire — same change as Incident.estimatedLosesAmount.
  totalReceivedAmount: number;
  currency: string;
}

export interface Package {
  id: string;
  incidentId: string;
  name: string;
  type: 'START' | 'RECOVER' | null;
  initialPaymentAmount: string;
  isEssential: boolean;
  isDeleted: boolean;
  proposedAt: number;
  postBlockingFee: string;
  isBlockingPercent: boolean;
  postRecoveryFee: string;
  isRecoveryPercent: boolean;
  paymentCurrency: CurrencyPayment;
  // Marketing display fields (Option A, shipped 2026-04-18).
  // All nullable — admin populates via PUT /offer-packages/{id}.
  // FE falls back to name / initialPaymentAmount when null.
  displayTitle?: string;
  displayOptions?: string[];
  displayCost?: string;
  additionalInfo?: string;
  imgUrl?: string;
  displayLabel?: string;
}

export interface CurrencyPayment {
  id: string;
  currency: string;
  network: string;
  description: string;
  decimals: number;
}

export interface Invoice {
  id: string;
  name: string;
  // Same InvoiceStatus enum as InvoiceSummary. Server-side predicates worth
  // mirroring: final = COMPLETED | EXPIRED | CANCELED, ready for payment =
  // PAYMENT_WAITING | PARTIALLY_PAID. POSTPONED has no amount and no address.
  status: 'POSTPONED' | 'PAYMENT_WAITING' | 'PARTIALLY_PAID' | 'COMPLETED' | 'EXPIRED' | 'CANCELED';
  incidentId: string;
  offerId: string;
  // JSON numbers on the wire (BE: OfferInvoiceResponse uses BigDecimal →
  // number serialization). The formatAmount pipe coerces both numbers and
  // legacy comma-strings, so the type tightening here is the source of
  // truth — callers should expect numbers.
  packageCost: number;
  packageName: string;
  packageType: 'START' | 'RECOVERY' | null;
  packageId: string;
  invoiceAddress: string;
  // Absent (key omitted, DTO is NON_NULL) while POSTPONED — the admin hasn't
  // finalized the amount, so there is nothing to pay and no deposit address.
  expectedAmount?: number;
  receivedAmount: number;
  currencyType: string;
  network: string;
  createdAt: number;
  expiredAt: number;
  completedAt: number;
  receivedTransactionsCount: number;
  lastTxTime: number;
  dueAmount: number;
  transactions: string[];
}

// Error body from the BE GlobalExceptionHandler. `code` is a stable
// ErrorCode; the HTTP status carrying a given code is not — duplicate
// transactions are 400 today and move to 409 later — so branch on the code.
export interface ApiError {
  code: number;
  subCode: number | null;
  message: string;
}

/** ErrorCode.ALREADY_EXIST — e.g. this hash is already on this case. */
export const ERROR_CODE_ALREADY_EXIST = 1111;

// Admin-configured app settings from GET /api/v1/settings. `value` is null
// until an admin fills the setting in, so every consumer must handle absence
// rather than assume a usable value.
export interface AppSetting {
  key: string;
  value: string | null;
}

export const SUPPORT_TELEGRAM_CHAT_URL = 'SUPPORT_TELEGRAM_CHAT_URL';

export interface IncidentReport {
  id: string;
  incidentId: string;
  name: string;
  mediaType: string;
  fileId: string;
  createdAt: number;
}

// User-side bundle returned from
// GET /api/v1/users/incidents/{incidentId}/preinvestigation-report.
// Empty fields rather than 404 when admin hasn't filled anything yet.
export interface PreinvestigationReport {
  report: {
    reportHtml: string | null;
    imageUrl: string | null;
    updatedAt: string | null;
  };
  recoveryOptions: PreinvestigationRecoveryOption[];
  // Multi-invoice flow: invoices live on the bundle so 30s polling sees
  // status changes (POSTPONED → PAYMENT_WAITING when admin finalizes) without
  // a separate fetch. The previous root-level offerId is gone — derive offer
  // context from incident.actualOfferId when calling POST /start-recovery.
  invoices: InvoiceSummary[];
}

export interface PreinvestigationRecoveryOption {
  id: string;
  sortOrder: number;
  displayTitle: string;
  displayLabel: string | null;
  displayOptions: string[];
  displayCost: string;
  additionalInfo: string | null;
  imgUrl: string | null;
  // Catalog template the row was materialized from. Display-only — never use
  // for selection keying or for the start-recovery body. Can repeat across
  // rows (multiple per-incident options materialized from one catalog
  // template) and can be null after V95 for hand-authored options.
  sourcePackageId: string | null;
  // Always true on the user-app endpoint (BE filters disabled options out).
  // Kept on the type so we can reuse it on admin app, where both flags ship.
  enabled: boolean;
  // True when this option is in a paid or in-flight invoice
  // (PAYMENT_WAITING / PARTIALLY_PAID / COMPLETED). Locked options cannot be
  // re-selected — they render with a status badge instead.
  isLocked: boolean;
  // The option's own fee (V95). Each option declares one kind of fee, and the
  // BE omits the fields it doesn't declare (NON_NULL), so they arrive absent.
  initialPaymentAmount?: number | null;
  postBlockingFee?: number | null;
  isBlockingPercent?: boolean;
  postRecoveryFee?: number | null;
  isRecoveryPercent?: boolean;
}

/**
 * Statuses the user can still pay into. PARTIALLY_PAID only started appearing
 * once the BE began assigning it — a part-paid invoice previously reported
 * PAYMENT_WAITING — so treat the two together everywhere rather than
 * re-deriving the pair at each call site.
 */
export function isInvoicePayable(status: InvoiceSummary['status']): boolean {
  return status === 'PAYMENT_WAITING' || status === 'PARTIALLY_PAID';
}

export interface InvoiceSummary {
  id: string;
  status: 'POSTPONED' | 'PAYMENT_WAITING' | 'PARTIALLY_PAID' | 'COMPLETED' | 'EXPIRED' | 'CANCELED';
  // Null while POSTPONED — admin hasn't finalized the amount yet.
  expectedAmount: string | null;
  receivedAmount: string | null;
  // Per-incident recovery option ids this invoice covers. A combined
  // FIXED_AMOUNT invoice carries N ids; per-option POSTPONED invoices carry
  // exactly one. Renamed from packageIds in V95 — these are
  // bundle.recoveryOptions[].id values, not catalog package ids.
  optionIds: string[];
  createdAt: string;
  completedAt: string | null;
}
